import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';
import {
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { selectMyLeadsSearchOptions } from '@mm/state/selectors/myLeadsSelectors';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import {
  FilterItem,
  FilterParams,
  TagsFilters,
} from '@mm/state/model/AudiencesModel';
import { ObjectUtils } from '@mm/utils';

export const getEnumeratedFilters = (
  params?: FilterParams | TagsFilters,
  withLogic?: boolean,
) => {
  const filterParamsWithValues = params
    ?.filter((f: FilterItem) => f.value?.length)
    .map(({ esType: _esType = null as any, ...filter }) => ({
      ...filter,
    }));

  const paramsToUse = withLogic ? filterParamsWithValues : params;

  return {
    ...(paramsToUse?.length && withLogic ? { logic: '1' } : {}),
    ...paramsToUse?.reduce((acc, filter, i) => {
      acc[i + 1] = filter;
      return acc;
    }, {}),
  };
};

export const useFilterMyLeads = ({
  filterParams,
  fetchAttributes = false,
}: {
  fetchAttributes?: boolean;
  filterParams: MyLeadsSearchQuery;
}) => {
  const { retrieveAttributes } = useMyLeadsCRUDActions();
  const { retrieveAll } = useContactsCRUDActions();
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);

  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const searchOptions = useSelector(selectMyLeadsSearchOptions);
  const searchVariablesFiltered = Object.entries(
    searchOptions.variables || {},
  ).filter(item => item[1].length);
  const searchVariablesList = Object.fromEntries(searchVariablesFiltered);
  const searchAttributes = searchOptions.attributes;
  const businessContactKey = convertToUpperSnakeCase(SubsectionKeys.leadType);

  useEffect(() => {
    (async () => {
      if (fetchAttributes) {
        setBusy(true);
        await retrieveAttributes({ skipBusy: true });
        setBusy(false);
      }
    })();
  }, []);

  const debouncedLeadsSearch = useCallback(
    () =>
      debounce(async () => {
        setBusy(true);

        const isExcludingBusinessContacts =
          !!filterParams.query?.excludedVariables?.[businessContactKey];
        const isIncludingBusinessContacts =
          !!filterParams.query?.variables?.[businessContactKey];
        const updatedFilterParams = filterParams;

        if (isExcludingBusinessContacts && isIncludingBusinessContacts) {
          delete updatedFilterParams.query?.excludedVariables?.[
            businessContactKey
          ];
          delete updatedFilterParams.query?.variables?.[businessContactKey];
        }

        for (const key in updatedFilterParams.query) {
          if (!Object.keys(updatedFilterParams.query[key]).length) {
            delete updatedFilterParams.query[key];
          }
        }

        const enumeratedFilterParams = getEnumeratedFilters(
          updatedFilterParams.filterParams,
          true,
        );
        const enumeratedTags = getEnumeratedFilters(
          updatedFilterParams.tagsFilters,
          false,
        );

        const hasFilterParams = !!Object.keys(enumeratedFilterParams).length;

        const hasESFilter = Object.values(
          updatedFilterParams?.query || {},
        )?.length;

        await retrieveAll(
          facebookPageId,
          filterParams.page || 1,
          undefined,
          undefined,
          {
            skipBusy: true,
            audience: {
              ...(hasESFilter && {
                esFilters: ObjectUtils.deepSnakeKeys(
                  updatedFilterParams?.query || {},
                  true,
                ),
              }),
              ...(hasFilterParams && { filterParams: enumeratedFilterParams }),
              ...(updatedFilterParams.tagsFilters?.length && {
                tagsFilters: enumeratedTags,
              }),
            },
            withDeliverable: true,
          },
        );
        setBusy(false);
      }, 300),
    [retrieveAll, filterParams, businessContactKey, facebookPageId],
  );

  useEffect(() => {
    if (mounted) {
      debouncedLeadsSearch()();
    } else {
      setMounted(true);
    }
  }, [filterParams, debouncedLeadsSearch]);

  return {
    searchAttributes,
    searchVariablesList,
    busy,
  };
};
